<script lang="ts" setup>
import type { HTMLAttributes } from 'vue';
import type { RouteLocationRaw } from 'vue-router';
import { cn } from '@/libs/css';
import type { MaterialSymbolName } from '@/types/materialSymbolNames';
import type { IconColour } from './BaseIcon.vue';

export type ButtonVariant = 'filled' | 'outline-strong' | 'outline' | 'text';
export type ButtonSize = 'small' | 'medium' | 'large';

const props = withDefaults(
  defineProps<{
    type: 'submit' | 'button';
    size?: ButtonSize;
    variant?: ButtonVariant;
    destructive?: boolean;
    active?: boolean;
    icon?: MaterialSymbolName;
    iconPosition?: 'left' | 'right';
    alt?: string;
    dropdown?: boolean;
    loading?: boolean;
    disabled?: boolean;
    route?: RouteLocationRaw;
    href?: string;
    class?: HTMLAttributes['class'];
  }>(),
  {
    size: 'medium',
    variant: 'outline',
    icon: undefined,
    iconPosition: 'left',
    alt: undefined,
    route: undefined,
    href: undefined,
    class: undefined,
  },
);

defineEmits<{
  click: [e: MouseEvent];
}>();

const componentType = computed(() => {
  if (props.route && !props.disabled) return 'router-link';
  if (props.href && !props.disabled) return 'a';
  return 'button';
});

const routeAttrs = computed(() => {
  if (props.route) {
    return {
      to: props.route,
    };
  }
  if (props.href) {
    return {
      href: props.href,
      target: '_blank',
      rel: 'noopener noreferred',
    };
  }
  return {};
});

const iconColor = computed<IconColour>(() => {
  if (props.disabled) return 'disabled';
  if (props.variant === 'filled') return 'on-fill';
  if (props.destructive) return 'error';
  return 'neutral';
});
</script>

<template>
  <component
    :is="componentType"
    v-bind="{ ...routeAttrs, ...$attrs }"
    :class="
      cn(
        'bb-button inline-block font-semibold no-underline',
        size,
        `bb-${variant}`,
        {
          destructive: !disabled && destructive,
          active: active && !disabled,
          disabled,
          dropdown,
        },
        props.class,
      )
    "
    :disabled="disabled || loading"
    active
    :type="route && !disabled ? null : type"
    @click="$emit('click', $event)"
  >
    <div v-if="loading" class="spinner">
      <BaseSpinner :size="size" :colour="variant === 'filled' ? 'on-fill' : 'neutral'" />
    </div>
    <span
      v-if="icon && iconPosition === 'left'"
      :class="{ invisible: loading, 'pr-1x': $slots.default }"
    >
      <BaseIcon :size="size" :name="icon" :colour="iconColor" />
    </span>
    <span :class="{ invisible: loading }"><slot /></span>
    <span v-if="alt" class="sr-only">{{ alt }}</span>
    <span
      v-if="(icon && iconPosition === 'right') || (!icon && href)"
      :class="{ invisible: loading, 'pl-1x': $slots.default }"
    >
      <BaseIcon
        :size="size"
        :name="href && !icon ? 'open_in_new' : icon"
        :colour="disabled ? undefined : iconColor"
      />
    </span>
    <span v-if="dropdown" class="dropdown" :class="{ invisible: loading }">
      <BaseIcon :size="size" name="keyboard_arrow_down" :colour="iconColor" />
    </span>
  </component>
</template>

<style scoped>
.bb-button {
  position: relative;
  box-sizing: border-box;
  font-family: inherit;
  line-height: var(--line-height-base);
  white-space: nowrap;
  cursor: pointer;
  border-style: solid;
  border-width: 1px;
  border-radius: var(--radius-sm);
  background-color: var(--background-color-default);
  color: var(--text-color-neutral);
  text-align: center;
  transition: all 150ms ease;

  &.small {
    padding: var(--spacing-1x) var(--spacing-2x);
    font-size: var(--text-sm);

    &.icon-button {
      line-height: 1;
      padding: var(--spacing-1\.5x);
    }
  }

  &.medium {
    padding: var(--spacing-1\.5x) var(--spacing-3x);
    font-size: var(--text-base);

    &.icon-button {
      padding: var(--spacing-1\.5x) calc(var(--spacing-1\.5x) + 1px);
    }

    &.dropdown {
      padding-right: var(--spacing-2x);
    }
  }

  &.large {
    padding: var(--spacing-2x) var(--spacing-4x);
    font-size: var(--text-lg);

    &.icon-button {
      padding: var(--spacing-2x);
    }
  }

  &.bb-filled {
    color: var(--text-color-on-fill);
    background-color: var(--background-color-strong-primary);
    /* stylelint-disable-next-line risk-ledger/enforce-semantic-variables */
    border-color: var(--background-color-strong-primary);

    &:hover {
      background-color: var(--background-color-strong-primary-hover);
    }

    &.disabled {
      background-color: var(--background-color-neutral-disabled);
    }

    &.destructive {
      background-color: var(--background-color-strong-error);
      /* stylelint-disable-next-line risk-ledger/enforce-semantic-variables */
      border-color: var(--background-color-strong-error);

      &:hover {
        background-color: var(--background-color-strong-error-hover);
      }
    }
  }

  &.bb-outline-strong {
    border-color: var(--border-color-strong-neutral-muted);

    &:hover {
      background-color: var(--background-color-hover);
    }

    &.destructive {
      border-color: var(--border-color-strong-error);

      &:hover {
        background-color: var(--background-color-error-hover);
      }
    }
  }

  &.bb-outline {
    border-color: var(--border-color-neutral);

    &:hover {
      background-color: var(--background-color-neutral-hover);
    }

    &.destructive {
      border-color: var(--border-color-error);

      &:hover {
        background-color: var(--background-color-error-hover);
      }
    }
  }

  /* we need a bit of extra specificity to override destructive buttons */
  &.disabled.disabled:not(.bb-text) {
    color: var(--text-color-neutral-disabled);
    border-color: var(--border-color-neutral-disabled);
  }

  &.bb-text {
    border-color: transparent;
    background-color: transparent;

    &:hover {
      border-color: var(--border-color-neutral);

      &.destructive {
        border-color: var(--border-color-error);
      }
    }

    &.disabled {
      color: var(--text-color-neutral-disabled);
    }
  }

  &:not(.bb-filled).destructive {
    color: var(--text-color-error);
  }

  .dropdown {
    display: inline-block;
    opacity: 0.6;
    margin-left: var(--spacing-1x);
  }
}

.spinner {
  position: absolute;
  inset: 0;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}
</style>
